import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "../RootStore";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import {
    ApplicationFilters,
    QuestionResultFilter,
} from "./ApplicationFiltersStore";
import { MetaLabelService } from "services/MetaLabelService";
import { AuthStore } from "stores/AuthStore";
import _, { startCase } from "lodash";
import { ApplicationFiltersInstanceStore } from "./ApplicationFiltersInstanceStore";
import { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import { IOutlineFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import { OrganizationMetadataField } from "models/OrganizationModels/OrganizationMetadataField";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { coreFields } from "services/MetadataService";
import ClassifierService from "services/ClassifierService";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import type { StatusDescriptor } from "components/UI/StatusTags";
import { isLegacyStatus } from "components/UI/StatusTags";
import {
    hasGroupedExtendedMetadataValues,
    isGroupableExtendedMetadataField,
} from "utils/extendedMetadata";
import { ApplicationFilterValuesStore } from "./ApplicationFilterValuesStore";

// A "filter key" for a filter that modifies more than 1
// filter value at a time. ex. dateRange would provide a component
// to modify  startDate, endDate, dateRangeLabel, and dateReferenceOption
type CompoundFilterKey = "callDuration" | "messageCount" | "dateRange";

export type FilterKey =
    | keyof ApplicationFilters
    | CompoundFilterKey
    | `extendedMetadata.${string}`;

export interface IApplicationFilterItem {
    title: string;
    filterKey: FilterKey;
    infoText?: string;
    /**
     * Status advertised next to the title, with optional hover copy. Two filters may deliberately
     * share a title — the two Theme filters do — in which case the legacy one's status is
     * the only thing distinguishing them.
     *
     * Distinct from `infoText` above, which explains the filter itself and renders on its own info
     * icon; this explains the status and renders on the tag.
     */
    status?: StatusDescriptor;
    /**
     * If the permission is a string[], then the user only needs access to 1 of the perms to view the filter.
     */
    permission?: string | string[];
}

export interface StripInaccessibleFiltersResult {
    filters: ApplicationFilters;
    removedInaccessibleFilters: boolean;
}

interface ApplicationFilterGroup {
    title: string;
    items: IApplicationFilterItem[];
}

/**
 * The derived group collecting filters the viewer sees as Deprecated. Kept out of
 * {@link filterGroupTitles} on purpose: that map types the statically declared groups, and
 * `getFilterGroup` throws for a title with no group behind it. This one has no static group — it is
 * assembled per viewer by `withLegacyGrouping`.
 */
export const LEGACY_FILTER_GROUP_TITLE = "Legacy Filters";

const filterGroupTitles = {
    basicFilters: "Standard Filters",
    mlModelIdentification: "Core AI Models",
    additionalMetadata: "Additional Metadata",
} as const;

type FilterGroupTitle =
    typeof filterGroupTitles[keyof typeof filterGroupTitles];

/**
 * The plain-text deprecation marker, kept in one place so every surface that appends it spells it
 * the same way.
 */
export const DEPRECATED_TITLE_MARKER = "(Deprecated)";

const defaultFilterGroups: ApplicationFilterGroup[] = [
    {
        title: filterGroupTitles.basicFilters,
        items: [
            {
                title: "AI-Powered Search",
                filterKey: "embeddedTranscriptionQuery",
                permission: "AI Search",
            },
            {
                title: "Agents",
                filterKey: "agentIds",
            },
            {
                title: "Agent Managers",
                filterKey: "agentManagerIds",
            },
            {
                title: "Call Direction",
                filterKey: "callDirections",
            },
            {
                title: "Call Duration",
                filterKey: "callDuration",
            },
            {
                title: "Message Count",
                filterKey: "messageCount",
            },
            {
                title: "Classifiers",
                filterKey: "rbcFilterItems",
            },
            {
                title: "Client Call ID",
                filterKey: "clientCallId",
            },
            {
                title: "Conversation Labeling",
                filterKey: "dataLabels",
                permission: ["Data Labeling", "Research"],
            },
            {
                title: "Date Range",
                filterKey: "dateRange",
            },
            {
                title: "Date Type",
                filterKey: "dateReferenceOption",
            },
            {
                title: "Evaluation Type",
                filterKey: "evaluationTypes",
            },
            {
                title: "Favorite Conversations",
                filterKey: "favoriteConversations",
            },
            {
                title: "Hierarchy",
                filterKey: "hierarchyIds",
            },
            {
                title: "Language",
                filterKey: "languages",
            },
            {
                title: "Media Type",
                filterKey: "mediaTypes",
            },
            {
                title: "Module Scores",
                filterKey: "moduleScores",
            },
            {
                title: "Question Results",
                filterKey: "questionResults",
            },
            {
                title: "Words or Phrases",
                filterKey: "wordsAndPhrasesSearchString",
            },
        ],
    },
    {
        title: filterGroupTitles.mlModelIdentification,
        items: [
            {
                title: "Contact Type",
                filterKey: "contactTypes",
            },
            {
                title: "Eddy Effect Signal",
                filterKey: "eddyEffectStatus",
            },
            {
                title: "Ending Sentiment",
                filterKey: "endingSentiment",
            },
            {
                title: "HIPAA Compliance",
                filterKey: "hipaaCompliance",
                permission: "HIPAA Compliance Model",
            },
            {
                title: "Model Results",
                filterKey: "modelResults",
                permission: "Model Results",
            },
            {
                title: "Outline",
                filterKey: "outlineSegmentFilterItems",
            },
            {
                title: "Patient Journey",
                filterKey: "patientJourneyStages",
                permission: "Patient Journey",
            },
            // Two Theme filters live side by side and are deliberately titled the same. They
            // differ only in Theme source and entitlement, and the Deprecated tag on the legacy
            // one is what tells them apart. See theme-split-decisions.md.
            {
                title: "Primary Theme",
                filterKey: "topics",
                permission: "Topics",
                status: {
                    tag: "Deprecated",
                    infoText:
                        "This is a legacy filter and will not populate data after 7/1/26. Use the new Primary Theme filter instead.",
                    permission: "Drivers",
                },
            },
            {
                title: "Primary Theme",
                filterKey: "themes",
                permission: "Drivers",
            },
            {
                title: "Primary Topic",
                filterKey: "primaryTopics",
                permission: "Topics",
                status: {
                    tag: "Deprecated",
                    infoText:
                        "Drivers replaced Topics on 7/1/26. Use the Primary Driver filter for conversations after that date.",
                    permission: "Drivers",
                },
            },
            {
                title: "Primary Driver",
                filterKey: "drivers",
                permission: "Drivers",
            },
            {
                title: "Safety Event",
                filterKey: "safetyEvent",
                permission: "Safety Events Model",
            },
            {
                title: "Starting Sentiment",
                filterKey: "beginningSentiment",
            },
        ],
    },
    {
        title: filterGroupTitles.additionalMetadata,
        // Meta 1-5 title are updated when meta labels are loaded
        // Extended metadata items are added once loaded
        items: [
            {
                title: "Meta 1",
                filterKey: "meta1",
            },
            {
                title: "Meta 2",
                filterKey: "meta2",
            },
            {
                title: "Meta 3",
                filterKey: "meta3",
            },
            {
                title: "Meta 4",
                filterKey: "meta4",
            },
            {
                title: "Meta 5",
                filterKey: "meta5",
            },
        ],
    },
];

@AcxStore
export class ApplicationFilterItemsStore extends BaseStore {
    public static readonly Tasks = {
        LOAD_META_LABELS: "Load Meta Labels",
        LOAD_AVAILABLE_METADATA_FIELDS: "Load Available Metadata Fields",
    };

    @observable
    public filterGroups: ApplicationFilterGroup[] =
        _.cloneDeep(defaultFilterGroups);

    private readonly authStore: AuthStore;

    private readonly classifierService = new ClassifierService();
    private readonly metaLabelService = new MetaLabelService();

    @observable
    public selectedFilterItems: IApplicationFilterItem[] = [];

    private organizationId: string;

    @observable
    public extendedMetadataFields: ExtendedMetadataClassifier[] = [];

    constructor(private rootStore: IRootStore) {
        super("Application Filter Items");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);

        reaction(
            () => this.authStore.orgStore.selectedOrganization,
            async (organization) => {
                if (!organization) return;

                this.organizationId = organization.id;

                this.resetForOrgChange();

                // stripInaccessibleFilters is synchronous, but deciding whether a
                // Question Result selection is allowed needs the module catalog.
                // Warm it here, once per org, so every call site answers from real
                // data instead of each having to remember to await a loader first.
                void this.filterValuesStore.loadQuestionResultFilterableModules();

                await this.loadMetadataLabels();
            },
            { fireImmediately: true },
        );
    }

    /**
     * Resolved on demand rather than held as a field: this store is constructed
     * before the reaction above first fires, and RootStore.getStore has no
     * re-entrancy guard, so nothing here should widen the construction graph.
     * getStore memoizes, making this a map lookup after the first call.
     */
    private get filterValuesStore(): ApplicationFilterValuesStore {
        return this.rootStore.getStore(ApplicationFilterValuesStore);
    }

    @computed get availableFilterGroups() {
        const groups: ApplicationFilterGroup[] = [];

        // if we don't have the authStore yet don't show any filters
        if (!this.authStore) return [];

        for (const group of this.filterGroups) {
            const remainingItems: IApplicationFilterItem[] = [];

            for (const item of group.items) {
                if (!this.canAccessFilterItem(item)) continue;

                const isSelected = this.selectedFilterItems.includes(item);

                if (!isSelected) remainingItems.push(item);
            }

            if (remainingItems.length > 0)
                groups.push({ title: group.title, items: remainingItems });
        }

        return this.withLegacyGrouping(groups);
    }

    /**
     * Moves every filter the viewer can see as Deprecated into one Legacy group at the end,
     * leaving the rest where they were declared.
     *
     * Membership is derived rather than declared, so a filter joins and leaves the Legacy group by
     * changing its own status — there is no second list to keep in step. It also means the group is
     * viewer-specific: a tenant that cannot see a Deprecated tag does not get a Legacy heading
     * collecting filters it has no explanation for. See {@link isLegacyStatus}.
     *
     * Returns the input untouched when nothing qualifies, so the group simply does not appear.
     */
    public withLegacyGrouping(
        groups: ApplicationFilterGroup[],
    ): ApplicationFilterGroup[] {
        if (!this.authStore) return groups;
        const canView = (resource: string) =>
            this.authStore.canUserView(resource);

        const legacyItems: IApplicationFilterItem[] = [];
        const remainingGroups: ApplicationFilterGroup[] = [];

        for (const group of groups) {
            const currentItems = group.items.filter((item) => {
                if (!isLegacyStatus(item.status, canView)) return true;
                legacyItems.push(item);
                return false;
            });

            if (currentItems.length > 0)
                remainingGroups.push({
                    title: group.title,
                    items: currentItems,
                });
        }

        if (legacyItems.length === 0) return groups;

        return [
            ...remainingGroups,
            { title: LEGACY_FILTER_GROUP_TITLE, items: legacyItems },
        ];
    }

    /**
     * Filter titles keyed by filter key, for surfaces that render a label as a plain string.
     *
     * The two Theme filters deliberately share the title "Primary Theme" and are told apart by
     * their status tag. A bare string cannot carry one, so the deprecated one picks up a marker
     * through {@link withDeprecationMarker} -- for the viewers who can see both of them -- and the
     * supported one keeps its plain title. Every other filter gets its title unchanged.
     */
    @computed get plainTextFilterTitleByKey(): Map<string, string> {
        const titleMap = new Map<string, string>();

        for (const group of this.filterGroups) {
            for (const item of group.items) {
                titleMap.set(
                    item.filterKey,
                    this.withDeprecationMarker(item.filterKey, item.title),
                );
            }
        }

        return titleMap;
    }

    /**
     * The declared filter item for a key, or undefined when nothing declares it.
     *
     * Exists for the surfaces that render a filter without holding its item — the Conversations
     * drawer keys its own registry off `storeObjectKey`, not `filterKey` — so they can read the
     * filter's status and info text from the one declaration instead of restating it. Says nothing
     * about access; pair it with {@link canAccessFilterItem} where visibility matters.
     */
    public getFilterItem(
        filterKey: FilterKey,
    ): IApplicationFilterItem | undefined {
        return this.filterGroups
            .flatMap((group) => group.items)
            .find((item) => item.filterKey === filterKey);
    }

    /**
     * Whether the filter behind a key should be presented as legacy for this viewer.
     *
     * The same question {@link withLegacyGrouping} asks of an item it already holds, for the
     * surfaces that only hold a key. The Conversations drawer carries its filters in its own shape
     * and cannot be run through `withLegacyGrouping`, so this is how it reaches the same answer
     * without restating the rule and letting the two drift.
     */
    public isLegacyFilterKey(filterKey?: FilterKey): boolean {
        if (!filterKey || !this.authStore) return false;

        return isLegacyStatus(
            this.getFilterItem(filterKey)?.status,
            (resource) => this.authStore.canUserView(resource),
        );
    }

    /**
     * Whether the filter behind a key is deprecated, for every viewer.
     *
     * Deliberately *not* {@link isLegacyFilterKey}, and the difference is the entitlement check.
     * That one answers a presentation question -- should this filter be *shown* as legacy -- so it
     * requires the viewer to be able to see the Deprecated tag, and a tenant without the
     * replacement is shown nothing they cannot act on. This one answers a question about the data:
     * the deprecated filters stopped populating on 7/1/26 for every tenant, so a capability that
     * cannot produce a meaningful answer over them has to be withheld from every tenant, including
     * the ones who never see the tag explaining why.
     *
     * Use this to gate behavior; use `isLegacyFilterKey` to gate presentation.
     */
    public isDeprecatedFilterKey(filterKey?: FilterKey): boolean {
        if (!filterKey) return false;

        return this.getFilterItem(filterKey)?.status?.tag === "Deprecated";
    }

    /**
     * Every declared filter key whose filter is deprecated, so readers can scan a filters object
     * without restating which those are. Derived from the declarations above, so deprecating
     * another filter reaches the readers on its own.
     */
    @computed get deprecatedFilterKeys(): readonly FilterKey[] {
        return this.filterGroups
            .flatMap((group) => group.items)
            .filter((item) => item.status?.tag === "Deprecated")
            .map((item) => item.filterKey);
    }

    /**
     * Titles that more than one filter *this viewer can access* declares.
     *
     * Accessibility is half the question. Two filters only collide on screen if both are actually
     * reachable: the two Theme filters share the title "Primary Theme", but a tenant without Drivers
     * can reach only one of them and so has nothing to disambiguate. See
     * {@link withDeprecationMarker}.
     */
    @computed
    private get titlesSharedByAccessibleFilters(): ReadonlySet<string> {
        const seenTitles = new Set<string>();
        const sharedTitles = new Set<string>();

        for (const group of this.filterGroups) {
            for (const item of group.items) {
                if (!this.canAccessFilterItem(item)) continue;
                if (seenTitles.has(item.title)) sharedTitles.add(item.title);
                else seenTitles.add(item.title);
            }
        }

        return sharedTitles;
    }

    /**
     * `label` marked "(Deprecated)", for the plain-string surfaces that cannot render a status tag.
     *
     * Three things have to hold, and all three are derived from what the filters already declare --
     * there is no list of marked filters to keep in step with the statuses:
     *
     * 1. The filter declares Deprecated.
     * 2. This viewer can see that it does, the same rule the tag itself follows
     *    ({@link isLegacyStatus}). A tenant shown no Deprecated tag anywhere should not meet the
     *    status for the first time as text they cannot act on.
     * 3. Another filter this viewer can access shares its title. The marker exists to tell two
     *    identically-titled filters apart, so a filter with a title of its own does not need it, and
     *    saying "(Deprecated)" there would only restate a status the tag already carries.
     *
     * In practice 2 and 3 move together: the entitlement that reveals the Deprecated tag is the same
     * one that reveals the replacement filter causing the collision. They are checked separately
     * because that is a fact about today's filters, not a rule.
     *
     * Returns `label` untouched whenever the marker does not apply, so call sites can wrap
     * unconditionally instead of repeating the check.
     */
    public withDeprecationMarker(
        filterKey: FilterKey | undefined,
        label: string,
    ): string {
        if (!filterKey) return label;
        if (!this.isLegacyFilterKey(filterKey)) return label;

        const title = this.getFilterItem(filterKey)?.title;
        if (!title || !this.titlesSharedByAccessibleFilters.has(title))
            return label;

        return `${label} ${DEPRECATED_TITLE_MARKER}`;
    }

    public canAccessFilterItem(item: IApplicationFilterItem): boolean {
        // Note: we are not using the permission string for this one because it
        // is an inverted permission. If the permission is present, the filters
        // should be hidden for non-ultra users.
        if (
            (item.filterKey === "moduleScores" ||
                item.filterKey === "evaluationTypes" ||
                item.filterKey === "questionResults") &&
            this.authStore.canUserView("Restrict Evaluations") &&
            !this.authStore.isUserUltra()
        ) {
            return false;
        }

        if (
            item.filterKey === "moduleScores" &&
            !this.authStore.canUserView("Module Scores")
        ) {
            return false;
        }

        if (Array.isArray(item.permission)) {
            return item.permission.some((permString) =>
                this.authStore.canUserView(permString),
            );
        }

        if (item.permission) {
            return this.authStore.canUserView(item.permission);
        }

        return true;
    }

    public stripInaccessibleFilters(
        filters: ApplicationFilters,
        options?: { requestObject?: boolean },
    ): StripInaccessibleFiltersResult {
        const sanitizedStore = new ApplicationFiltersInstanceStore(
            this.rootStore,
            "Application Filters Permission Strip Temp",
        );

        sanitizedStore.applyExternalFilters(_.cloneDeep(filters), false);
        const appliedFilterKeys = new Set(
            this.getAppliedFilterItems(sanitizedStore).map(
                (item) => item.filterKey,
            ),
        );
        let removedInaccessibleFilters = false;

        for (const item of this.filterGroups.flatMap((group) => group.items)) {
            if (this.canAccessFilterItem(item)) continue;
            if (appliedFilterKeys.has(item.filterKey)) {
                removedInaccessibleFilters = true;
            }

            if (item.filterKey === "dateRange") {
                sanitizedStore.removeDateRange();
            } else {
                sanitizedStore.resetByKey(item.filterKey);
            }

            // Clear companion flags so an inaccessible filter cannot survive as
            // an orphaned include/exclude mode in requests or chip rendering.
            if (item.filterKey === "topics") {
                sanitizedStore.setContainsTopics(true);
            } else if (item.filterKey === "themes") {
                sanitizedStore.setContainsThemes(true);
            } else if (item.filterKey === "primaryTopics") {
                sanitizedStore.setContainsPrimaryTopics(true);
            } else if (item.filterKey === "drivers") {
                sanitizedStore.setContainsDrivers(true);
            } else if (item.filterKey === "dataLabels") {
                sanitizedStore.setContainsConversationLabels(undefined);
                sanitizedStore.containsResearchLabels = undefined;
            }
        }

        // Question Results is visible as a whole, so the loop above cannot judge it:
        // access is decided per row, by the category of the module each row names.
        // Until the catalog has loaded there is nothing to judge against, so rows
        // are left alone rather than stripped on a guess.
        const currentQuestionResults = sanitizedStore.questionResults ?? [];
        if (
            currentQuestionResults.length > 0 &&
            this.filterValuesStore.hasLoadedQuestionResultLicensedModules
        ) {
            const accessibleQuestionResults: QuestionResultFilter[] = [];
            let removedAppliedQuestionResult = false;

            for (const questionResult of currentQuestionResults) {
                if (
                    this.filterValuesStore.canUseQuestionResultModule(
                        questionResult.licensedModuleId,
                    )
                ) {
                    accessibleQuestionResults.push(questionResult);
                    continue;
                }

                // A row without tags is an unfinished selection that requests and
                // chips already discard, so removing it is not a lost filter.
                if (questionResult.tagIds.length > 0) {
                    removedAppliedQuestionResult = true;
                }
            }

            if (
                accessibleQuestionResults.length !== currentQuestionResults.length
            ) {
                removedInaccessibleFilters ||= removedAppliedQuestionResult;
                sanitizedStore.setQuestionResults(accessibleQuestionResults);
            }
        }

        const sanitizedFilters = options?.requestObject
            ? sanitizedStore.toRequestObject()
            : sanitizedStore.toFilterObject();

        return {
            filters: sanitizedFilters,
            removedInaccessibleFilters,
        };
    }

    /**
     * Given a set of filters, returns an array of filter items
     * that had a corresponding value in the provided filters.
     * @param filters
     */
    getAppliedFilterItems(filters: ApplicationFiltersInstanceStore) {
        const filterSnapshot = filters.toFilterObject();
        const filterItems = this.filterGroups.flatMap((group) => group.items);
        const selectedItems: IApplicationFilterItem[] = [];

        for (const filterItem of filterItems) {
            if (filterItem.filterKey === "dateReferenceOption") {
                if (
                    filterSnapshot.dateReferenceOption !==
                    DateReferenceOption.InteractionDate
                )
                    selectedItems.push(filterItem);
                continue;
            }
            if (filterItem.filterKey === "dateRange") {
                if (filterSnapshot.startDate && filterSnapshot.endDate)
                    selectedItems.push(filterItem);
                continue;
            }
            if (filterItem.filterKey === "callDuration") {
                if (
                    !!filterSnapshot.minCallDuration ||
                    !!filterSnapshot.maxCallDuration
                )
                    selectedItems.push(filterItem);
                continue;
            }
            if (filterItem.filterKey === "messageCount") {
                if (
                    !!filterSnapshot.minMessageCount ||
                    !!filterSnapshot.maxMessageCount
                )
                    selectedItems.push(filterItem);
                continue;
            }
            if (
                filterItem.filterKey === "hierarchyIds" &&
                !!filterSnapshot.hierarchyIds &&
                filterSnapshot.hierarchyIds.length > 0
            ) {
                selectedItems.push(filterItem);
                continue;
            }

            const isExtendedMetadataItem =
                filterItem.filterKey.startsWith("extendedMetadata.");
            if (isExtendedMetadataItem) {
                const isGroupable = isGroupableExtendedMetadataField(
                    this.extendedMetadataFields,
                    filterItem.filterKey,
                );
                if (
                    isGroupable &&
                    hasGroupedExtendedMetadataValues(
                        filterSnapshot,
                        filterItem.filterKey,
                    )
                ) {
                    selectedItems.push(filterItem);
                    continue;
                }
            }

            if (!_.has(filterSnapshot, filterItem.filterKey)) continue;

            const filterValue = _.get(filterSnapshot, filterItem.filterKey);
            if (filterValue === undefined || filterValue === null) continue;
            if (Array.isArray(filterValue) && filterValue.length === 0)
                continue;
            if (typeof filterValue === "string" && filterValue === "") continue;

            if (
                filterItem.filterKey === "rbcFilterItems" &&
                (filterValue as IRBCFilter[])[0]?.classifierIds.length === 0
            )
                continue;
            if (
                filterItem.filterKey === "outlineSegmentFilterItems" &&
                (filterValue as IOutlineFilter[]).every(
                    (item) => item.segments.length === 0,
                )
            )
                continue;
            selectedItems.push(filterItem);
        }

        return selectedItems;
    }

    @action
    updateSelectedFilterItems(filters: ApplicationFiltersInstanceStore) {
        this.selectedFilterItems = this.getAppliedFilterItems(filters);
    }

    @action
    private resetForOrgChange() {
        this.selectedFilterItems = [];
        this.filterGroups = _.cloneDeep(defaultFilterGroups);
    }

    @action
    public setSelectedFilterItems(filterItems: IApplicationFilterItem[]) {
        this.selectedFilterItems = filterItems;
    }

    @action
    public addSelectedFilterItem(filterItem: IApplicationFilterItem) {
        this.selectedFilterItems.push(filterItem);
    }

    @action
    public removeSelectedFilterItem(filterItem: IApplicationFilterItem) {
        const index = this.selectedFilterItems.findIndex(
            (item) =>
                item.title === filterItem.title &&
                item.filterKey === filterItem.filterKey,
        );

        if (index < 0) return;

        const newItems = [...this.selectedFilterItems];
        newItems.splice(index, 1);

        this.selectedFilterItems = newItems;
    }

    public getFilterGroup(
        groupTitle: FilterGroupTitle,
    ): ApplicationFilterGroup {
        const group = this.filterGroups.find((g) => g.title === groupTitle);

        if (!group) throw new Error("Invalid filter title");

        return group;
    }

    /**
     * Loads this organizations metadata labels for meta 1 - 5 as well as
     * available extended metadata fields and adds them to filterGroups
     * under the additional metadata group
     */
    @action
    public async loadMetadataLabels() {
        const organizationIdAtRequest = String(this.organizationId);

        const metaLabelsTask = this.setupAsyncTask(
            ApplicationFilterItemsStore.Tasks.LOAD_META_LABELS,
            async () => {
                const metaLabels = await this.metaLabelService.getMetaLabels();

                const additionalMetadataGroup = this.getFilterGroup(
                    filterGroupTitles.additionalMetadata,
                );

                if (organizationIdAtRequest !== this.organizationId) return;

                for (const [metaKey, metaLabel] of Object.entries(metaLabels)) {
                    const metaItem = additionalMetadataGroup.items.find(
                        (item) => item.filterKey === metaKey.toLowerCase(),
                    );
                    if (!metaItem) continue;

                    metaItem.title = metaLabel;
                }
            },
        );

        const availableMetaFieldsTask = this.setupAsyncTask(
            ApplicationFilterItemsStore.Tasks.LOAD_AVAILABLE_METADATA_FIELDS,
            async () => {
                // get meta fields for org
                const extendedMetadataFields =
                    await this.classifierService.getExtendedMetadataStringClassifiers();

                this.extendedMetadataFields = extendedMetadataFields;

                // check if call direction meta field is found
                const hasCallDirectionMetaField = coreFields.some(
                    (field) =>
                        field.label ===
                        OrganizationMetadataField[
                            OrganizationMetadataField.CallDirection
                        ],
                );

                // check if client call id meta field is found
                const hasClientCallIdMetaField = coreFields.some(
                    (field) =>
                        field.label ===
                        OrganizationMetadataField[
                            OrganizationMetadataField.ClientCallId
                        ],
                );

                // get the basic filters group, its where call dir and client
                // call id filter items live
                const basicFiltersGroup = this.getFilterGroup(
                    filterGroupTitles.basicFilters,
                );

                // if we dont have call direction meta field, remove from basic filters
                if (!hasCallDirectionMetaField)
                    basicFiltersGroup.items = basicFiltersGroup.items.filter(
                        (item) => item.filterKey !== "callDirections",
                    );
                // if we dont have client call id meta field, remove from basic filters
                if (!hasClientCallIdMetaField)
                    basicFiltersGroup.items = basicFiltersGroup.items.filter(
                        (item) => item.filterKey !== "clientCallId",
                    );

                // Get additional meta group and add all extended meta fields
                const additionalMetadataGroup = this.getFilterGroup(
                    filterGroupTitles.additionalMetadata,
                );

                if (organizationIdAtRequest !== this.organizationId) return;

                for (const field of extendedMetadataFields) {
                    additionalMetadataGroup.items.push({
                        title: startCase(field.name),
                        filterKey: `extendedMetadata.${field.name}`,
                    });
                }
            },
        );

        const results = await Promise.all([
            metaLabelsTask,
            availableMetaFieldsTask,
        ]);

        const additionalMetadataGroup = this.getFilterGroup(
            filterGroupTitles.additionalMetadata,
        );

        additionalMetadataGroup.items.sort((a, b) =>
            a.title
                .toLocaleLowerCase()
                .localeCompare(b.title.toLocaleLowerCase()),
        );

        return results;
    }
}
